import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { CalendarEvent } from '../../../mocks/eventsCalendar';
import type { TMMUser } from '../../../hooks/useAuth';

interface RsvpModalProps {
  event: CalendarEvent;
  user: TMMUser;
  onClose: () => void;
}

type TicketType = 'general' | 'vip';
type Step = 'tickets' | 'upsell' | 'info' | 'success';

const GA_PRICE = 5;

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function buildGoogleCalendarUrl(ev: CalendarEvent): string {
  const start = ev.date.replace(/-/g, '') + 'T' + ev.time.replace(/[:\s]/g, '');
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&dates=${start}/${start}&location=${encodeURIComponent(ev.address)}&details=${encodeURIComponent(ev.description)}`;
}

const STEP_LABELS: Record<Exclude<Step, 'success'>, string> = {
  tickets: 'Tickets',
  upsell: 'Membership',
  info: 'Your Info',
};

const STEP_ORDER: Exclude<Step, 'success'>[] = ['tickets', 'upsell', 'info'];

function StepIndicator({ current }: { current: Step }) {
  if (current === 'success') return null;
  const idx = STEP_ORDER.indexOf(current as Exclude<Step, 'success'>);
  return (
    <div className="flex items-center gap-0 px-6 pt-4 pb-3">
      {STEP_ORDER.map((s, i) => (
        <div key={s} className="flex items-center gap-0">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                i < idx
                  ? 'bg-pink-500 text-white'
                  : i === idx
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {i < idx ? <i className="ri-check-line text-xs" /> : i + 1}
            </div>
            <span
              className={`text-[10px] font-semibold whitespace-nowrap hidden sm:block ${
                i === idx ? 'text-gray-900' : i < idx ? 'text-pink-500' : 'text-gray-400'
              }`}
            >
              {STEP_LABELS[s]}
            </span>
          </div>
          {i < STEP_ORDER.length - 1 && (
            <div className={`w-8 h-px mx-2 ${i < idx ? 'bg-pink-300' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function RsvpModal({ event, user, onClose }: RsvpModalProps) {
  const navigate = useNavigate();
  const [ticketType, setTicketType] = useState<TicketType>('general');
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState(user.firstName);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<Step>('tickets');

  useEffect(() => {
    setName(user.firstName);
    setEmail(user.email);
  }, [user]);

  const unitPrice = ticketType === 'general' ? GA_PRICE : 0;
  const total = unitPrice * quantity;
  const isFree = total === 0;

  const handleTicketsContinue = () => {
    if (ticketType === 'general') {
      setStep('upsell');
    } else {
      setStep('info');
    }
  };

  const handleConfirm = async () => {
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setError('');
    setLoading(true);
    try {
      const { error: dbError } = await supabase.from('rsvps').insert({
        user_id: user.id,
        event_id: event.id,
        event_title: event.title,
        event_date: event.date,
        event_time: event.time,
        event_location: event.location,
        ticket_type: ticketType,
        quantity,
        unit_price: unitPrice,
        total_amount: total,
        attendee_name: name.trim(),
        attendee_email: email.trim(),
        attendee_phone: phone.trim() || null,
        notes: notes.trim() || null,
        payment_status: 'free',
      });
      if (dbError) throw dbError;
      setStep('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = () => {
    setError('Stripe payment is coming soon — connect Stripe to enable paid tickets.');
  };

  const handleUpgradeToVip = () => {
    onClose();
    navigate('/membership');
  };

  /* ── SUCCESS ── */
  if (step === 'success') {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="h-1.5 w-full bg-gradient-to-r from-pink-400 to-rose-500" />
          <div className="px-7 pt-8 pb-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-pink-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              You&apos;re in! We&apos;ll see you there.
            </h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Your spot is confirmed for <strong className="text-gray-800">{event.title}</strong>.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 text-left mb-6 space-y-2.5">
              {[
                { icon: 'ri-calendar-event-line', label: 'Date', value: formatDate(event.date) },
                { icon: 'ri-time-line', label: 'Time', value: `${event.time}${event.endTime ? ` – ${event.endTime}` : ''}` },
                { icon: 'ri-map-pin-line', label: 'Location', value: event.location, sub: event.address },
                {
                  icon: 'ri-ticket-2-line',
                  label: 'Tickets',
                  value: `${quantity} × ${ticketType === 'general' ? 'General Admission' : 'VIP'} — ${isFree ? 'Free' : `$${total}`}`,
                },
              ].map(({ icon, label, value, sub }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-pink-100 rounded-lg shrink-0">
                    <i className={`${icon} text-pink-500 text-sm`} />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 font-medium">{label}</div>
                    <div className="text-sm font-semibold text-gray-800">{value}</div>
                    {sub && <div className="text-xs text-gray-500">{sub}</div>}
                  </div>
                </div>
              ))}
            </div>
            <a
              href={buildGoogleCalendarUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap mb-3"
            >
              <i className="ri-calendar-check-line text-base" />
              Add to Calendar
            </a>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── MODAL SHELL ── */
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1.5 w-full bg-gradient-to-r from-pink-400 to-rose-500" />

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-6 pt-5 pb-0 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-ticket-2-line text-pink-500 text-base" />
            </div>
            <span className="text-[11px] font-bold text-pink-500 uppercase tracking-widest">RSVP</span>
          </div>
          <h2 className="text-lg font-bold text-gray-900 leading-snug pr-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            {event.title}
          </h2>
          <div className="flex flex-wrap items-center gap-3 mt-1.5 mb-3">
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-calendar-line text-pink-400 text-sm" />{formatDate(event.date)}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-time-line text-pink-400 text-sm" />{event.time}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-map-pin-line text-pink-400 text-sm" />{event.location}
            </span>
          </div>
          <StepIndicator current={step} />
        </div>

        {/* ── STEP 1: TICKETS ── */}
        {step === 'tickets' && (
          <div className="px-6 py-5 space-y-5">
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Select Ticket Type</h3>
              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => setTicketType('general')}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${ticketType === 'general' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${ticketType === 'general' ? 'bg-pink-100' : 'bg-gray-100'}`}>
                      <i className={`ri-ticket-line text-base ${ticketType === 'general' ? 'text-pink-500' : 'text-gray-500'}`} />
                    </div>
                    <div className="text-left">
                      <div className={`text-sm font-bold ${ticketType === 'general' ? 'text-pink-700' : 'text-gray-800'}`}>General Admission</div>
                      <div className="text-xs text-gray-500">Standard entry · limited spots</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-black ${ticketType === 'general' ? 'text-pink-600' : 'text-gray-800'}`}>$5</div>
                    <div className="text-[10px] text-gray-400">per attendee</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setTicketType('vip')}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${ticketType === 'vip' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${ticketType === 'vip' ? 'bg-rose-100' : 'bg-gray-100'}`}>
                      <i className={`ri-vip-crown-fill text-base ${ticketType === 'vip' ? 'text-rose-500' : 'text-gray-500'}`} />
                    </div>
                    <div className="text-left">
                      <div className={`text-sm font-bold ${ticketType === 'vip' ? 'text-rose-700' : 'text-gray-800'}`}>VIP — Members Only</div>
                      <div className="text-xs text-rose-400 font-medium">Priority access · First in line</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-black ${ticketType === 'vip' ? 'text-rose-600' : 'text-gray-800'}`}>Free</div>
                    <div className="text-[10px] text-gray-400">w/ membership</div>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Number of Attendees</h3>
              <div className="flex items-center gap-4">
                <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 cursor-pointer transition-colors">
                  <i className="ri-subtract-line" />
                </button>
                <span className="text-2xl font-black text-gray-900 w-8 text-center">{quantity}</span>
                <button type="button" onClick={() => setQuantity((q) => Math.min(10, q + 1))} className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 cursor-pointer transition-colors">
                  <i className="ri-add-line" />
                </button>
                <span className="text-xs text-gray-400 ml-1">
                  {ticketType === 'general' ? `× $${GA_PRICE} = $${GA_PRICE * quantity}` : '× Free = $0'}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleTicketsContinue}
              className="w-full py-3.5 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              Continue
              <i className="ri-arrow-right-line text-base" />
            </button>
          </div>
        )}

        {/* ── STEP 2: VIP UPSELL ── */}
        {step === 'upsell' && (
          <div className="px-6 py-5 space-y-5">
            {/* Upsell headline block */}
            <div className="text-center pt-1">
              <div className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-600 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-3">
                <i className="ri-vip-crown-fill text-xs" />
                Members get more
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1.5 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                Want early access &amp; better pricing?
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Members get priority RSVP, exclusive events, and tickets for free — every time.
              </p>
            </div>

            {/* Comparison table */}
            <div className="grid grid-cols-2 gap-3">
              {/* GA column */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 text-center border-b border-gray-200">
                  <div className="w-7 h-7 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-1.5">
                    <i className="ri-ticket-line text-gray-500 text-sm" />
                  </div>
                  <div className="text-xs font-bold text-gray-600">General</div>
                  <div className="text-lg font-black text-gray-800 mt-0.5">$5</div>
                  <div className="text-[10px] text-gray-400">per event</div>
                </div>
                <div className="px-3 py-3 space-y-2.5">
                  {[
                    { icon: 'ri-close-circle-line', text: 'Standard entry', muted: true },
                    { icon: 'ri-close-circle-line', text: 'Limited spots', muted: true },
                    { icon: 'ri-close-circle-line', text: 'No priority', muted: true },
                    { icon: 'ri-close-circle-line', text: 'Paid every time', muted: true },
                  ].map(({ icon, text }) => (
                    <div key={text} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${icon} text-gray-300 text-sm`} />
                      </div>
                      <span className="text-[11px] text-gray-400 leading-tight">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* VIP column */}
              <div className="border-2 border-rose-400 rounded-2xl overflow-hidden relative">
                <div className="absolute -top-px left-1/2 -translate-x-1/2">
                  <div className="bg-rose-500 text-white text-[9px] font-bold px-3 py-0.5 rounded-b-md whitespace-nowrap">
                    BEST VALUE
                  </div>
                </div>
                <div className="bg-rose-50 px-4 py-3 text-center border-b border-rose-100">
                  <div className="w-7 h-7 flex items-center justify-center bg-rose-200 rounded-full mx-auto mb-1.5">
                    <i className="ri-vip-crown-fill text-rose-600 text-sm" />
                  </div>
                  <div className="text-xs font-bold text-rose-600">VIP Member</div>
                  <div className="text-lg font-black text-rose-700 mt-0.5">Free</div>
                  <div className="text-[10px] text-rose-400">every event</div>
                </div>
                <div className="px-3 py-3 space-y-2.5">
                  {[
                    'Early RSVP access',
                    'Free tickets always',
                    'Priority spot secured',
                    'Members-only events',
                  ].map((text) => (
                    <div key={text} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-rose-500 text-sm" />
                      </div>
                      <span className="text-[11px] text-gray-700 font-medium leading-tight">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Savings callout */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-100 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-rose-100 rounded-full shrink-0">
                <i className="ri-money-dollar-circle-line text-rose-500 text-lg" />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-800">Save on every event you attend</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Members attending just 2 events per month save $10+ — membership pays for itself.
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="space-y-2.5 pt-1">
              <button
                type="button"
                onClick={handleUpgradeToVip}
                className="w-full py-3.5 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-vip-crown-line text-base" />
                Upgrade to VIP Membership
                <i className="ri-arrow-right-line text-base" />
              </button>
              <button
                type="button"
                onClick={() => setStep('info')}
                className="w-full py-3 border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                Continue with General Admission ($5)
              </button>
            </div>

            <button
              type="button"
              onClick={() => setStep('tickets')}
              className="w-full flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer py-1"
            >
              <i className="ri-arrow-left-line text-sm" />
              Back to tickets
            </button>
          </div>
        )}

        {/* ── STEP 3: INFO + CHECKOUT ── */}
        {step === 'info' && (
          <div className="px-6 py-5 space-y-5">
            {/* Mini order recap */}
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-pink-100 rounded-lg shrink-0">
                  <i className={`${ticketType === 'vip' ? 'ri-vip-crown-fill text-rose-500' : 'ri-ticket-line text-pink-500'} text-sm`} />
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-700">
                    {ticketType === 'general' ? 'General Admission' : 'VIP'} × {quantity}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {ticketType === 'general' ? `$${GA_PRICE} per ticket` : 'Free with membership'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-black text-gray-900">
                  {isFree ? 'FREE' : `$${total}`}
                </div>
                <button
                  type="button"
                  onClick={() => setStep(ticketType === 'general' ? 'upsell' : 'tickets')}
                  className="text-[10px] text-pink-500 hover:text-pink-700 font-semibold cursor-pointer"
                >
                  Change
                </button>
              </div>
            </div>

            {/* Attendee Info */}
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Your Info</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Email <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Phone <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 000-0000"
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Notes <span className="text-gray-400 font-normal">(allergies, requests)</span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                    placeholder="Any notes for the organizer..."
                    rows={2}
                    maxLength={500}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300 transition-all resize-none"
                  />
                  <div className="text-right text-[10px] text-gray-400 mt-0.5">{notes.length}/500</div>
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-100" />

            {/* Order total */}
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs text-gray-500">
                  {ticketType === 'general' ? 'General Admission' : 'VIP'} × {quantity}
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {isFree ? 'Free' : `$${total.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-sm font-bold text-gray-800">Total</span>
                <span className="text-xl font-black text-gray-900">
                  {isFree ? '$0.00' : `$${total.toFixed(2)}`}
                </span>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-3">
                <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {isFree ? (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="w-full py-3.5 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin text-base" /> Confirming your spot...</>
                ) : (
                  <><i className="ri-checkbox-circle-line text-base" /> Confirm RSVP — Free</>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStripePayment}
                className="w-full py-3.5 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-secure-payment-line text-base" />
                Pay ${total.toFixed(2)} — Powered by Stripe
              </button>
            )}

            <p className="text-center text-[10px] text-gray-400 flex items-center justify-center gap-1">
              <i className="ri-lock-line text-gray-300 text-xs" />
              Secure checkout · Your data is always protected
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
